import React, { useState } from 'react';
import SpendPacingChart from './SpendPacingChart';
import CampaignDeliveryCards, { SETTINGS_COLUMN_WIDTH, rangeCardStats } from './CampaignDeliveryCards';

/**
 * The campaign performance block at the top of both campaign forms' Settings tab:
 * the spend chart, then the summary squares under it.
 *
 * Extracted because both forms (adcampaigns.js, advertiser/campaigns.js) compose
 * the identical block, and the LAYOUT is the part that has to stay identical - a
 * wrapper duplicated in two files is how one screen ends up looking different
 * from the other.
 *
 * WIDTH IS CONSTRAINED, and that is the whole fix for wide screens. Left to fill
 * the viewport, a 24-bar chart at ~1960px by 130px is unreadably flat and the
 * summary squares - which deliberately do not grow - float in dead space beside
 * it. Bounded and centred, the chart keeps a sane aspect ratio at any monitor
 * size and the squares sit under a chart of their own width.
 *
 * The squares stay UNDER the chart rather than beside it: they are the same
 * campaign read at different grains, so they belong on one line below the shape
 * over time, not competing with it for horizontal space.
 *
 * The two halves read from DIFFERENT sources, deliberately:
 *   - the chart fetches a ClickHouse series, and its selected range feeds the
 *     fourth square, which has NO cap (the caps are hourly, daily and lifetime,
 *     and none of them bounds an arbitrary range)
 *   - the first three squares read the budget coordinator's own counters off the
 *     record, and are the only place spend is shown AGAINST a cap
 * The fourth square says "no cap for a custom range" in words for exactly that
 * reason - a range total read as cap progress is the class of bug NBU-7 fixed.
 */

// The panel is exactly as wide as the row of summary squares beneath the chart,
// because the chart is 100% of the panel and therefore inherits this width.
//
// A viewport percentage was tried and is wrong: the squares cap themselves at a
// FIXED natural width, so at any viewport where 60% exceeded that, the chart's
// right edge overshot the last square by however much the two happened to differ.
// Sizing from the squares makes the two edges agree by construction at every
// window size instead of by coincidence at one.
//
// The constant lives in CampaignDeliveryCards because that is where the numbers
// defining it live, and because the status banner stacked above this panel needs
// the same width - three pieces, one edge. On a 1920px monitor it lands near 57%,
// which is the "60% and centred" shape asked for.
const PANEL_WIDTH = SETTINGS_COLUMN_WIDTH;

const CampaignPerformancePanel = ({ record, campaignId, hourlyLimit, dailyLimit }) => {
  // Owned here, reported by the chart: the chart holds the range state and the
  // series, so it is the single source for what "the selected range" means.
  const [range, setRange] = useState(null);

  return (
    <div style={{
      width: PANEL_WIDTH,
      // Narrower viewports win: the squares wrap to two rows and the chart
      // shrinks with them rather than forcing a horizontal scrollbar.
      maxWidth: '100%',
      margin: '0 auto 8px',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    }}>
      <SpendPacingChart title="Performance" width="100%"
        campaignId={campaignId}
        hourlyLimit={hourlyLimit}
        dailyLimit={dailyLimit}
        onRangeChange={setRange} />
      <CampaignDeliveryCards record={record} rangeStats={rangeCardStats(range)} />
    </div>
  );
};

export default CampaignPerformancePanel;
